import { isExpiryStillAvailable } from '@/features/chart/lib/utils/option-util'
import { resolveOptionInstrument } from '@/features/order/order-form/lib/parse-option-instrument'
import type { OrderFormSelection } from '@/state/order'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'

/**
 * The Order Form's rows (its selection atom), as stored in the widget config's `orderRows`
 * key so rows added from the watchlist/chart/option chain survive a reload.
 *
 * Only what identifies a row is kept: instrument, side, account binding, metadata. Sizes are
 * left out on purpose (a restored ticket starts unsized), as are live prices and tickers,
 * which would be stale on load.
 */
export const ORDER_ROWS_CONFIG_KEY = 'orderRows'

function toStoredOption({
  ticker: _ticker,
  amount: _amount,
  lockedAmount: _lock,
  ...option
}: SelectedOption) {
  return { ...option, price: '-' }
}

function toStoredInstrument({
  amount: _amount,
  lockedAmount: _lock,
  lockedAmountUnit: _unit,
  ...instrument
}: SelectedInstrument) {
  return { ...instrument, lastPrice: '-' }
}

export function serializeOrderRows(selection: OrderFormSelection): string {
  return JSON.stringify({
    selectedOptions: selection.selectedOptions.map(toStoredOption),
    selectedInstruments: selection.selectedInstruments.map(toStoredInstrument),
  })
}

function isLiveOption(option: SelectedOption): boolean {
  // No metadata at restore time, so this only reads Deribit-style names; a row whose name
  // can't be parsed is kept rather than guessed at.
  const parsed = resolveOptionInstrument(option.instrumentName)
  return !parsed || isExpiryStillAvailable(parsed.expiry)
}

function isLiveInstrument(instrument: SelectedInstrument): boolean {
  const expiry = Number(instrument.expirationTsMs)
  return !instrument.expirationTsMs || !Number.isFinite(expiry) || expiry > Date.now()
}

/**
 * Reads a stored `orderRows` value back into a selection, dropping rows whose contract has
 * expired since it was saved. Returns null for a missing, empty, or malformed value.
 */
export function parseOrderRows(raw: string | undefined): OrderFormSelection | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<OrderFormSelection> | null
    const options = Array.isArray(parsed?.selectedOptions) ? parsed.selectedOptions : []
    const instruments = Array.isArray(parsed?.selectedInstruments) ? parsed.selectedInstruments : []
    const selection: OrderFormSelection = {
      selectedOptions: options
        .filter(
          (option): option is SelectedOption =>
            typeof option?.instrumentName === 'string' && isLiveOption(option)
        )
        .map(toStoredOption),
      selectedInstruments: instruments
        .filter(
          (instrument): instrument is SelectedInstrument =>
            typeof instrument?.symbol === 'string' && isLiveInstrument(instrument)
        )
        .map(toStoredInstrument),
    }
    return selection.selectedOptions.length + selection.selectedInstruments.length > 0
      ? selection
      : null
  } catch {
    return null
  }
}
